import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import TripPlaceFieldDesktop from './TripDetailsFields/TripPlaceFieldDesktop'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import { ModalHeaderWrapper } from 'components/Luxkit/Modal/ModalHeaderWrapper'
import { GlobalSearchDispatchContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import useStateWithRef from 'hooks/useStateWithRef'
import noop from 'lib/noop'
import { UpdateTripError } from 'tripPlanner/api/types'
import { useUpdateTrip } from 'tripPlanner/hooks/api'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'

interface Props {
  tripId: string
}

const UpdateTripOrigin = ({ tripId }: Props) => {
  const [tripOrigin, setTripOrigin, tripOriginRef] = useStateWithRef<
    { id: string; name: string } | undefined
  >(undefined)
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)

  const inputRef = useRef<HTMLInputElement>(null)
  const { closeModal, goBack } = useModalCallbacks()

  const { mutate, isLoading: isSaving } = useUpdateTrip({
    onSuccess: () => {
      closeModal()
    },
    onError: (e: UpdateTripError) => {
      if (e.status === 422) {
        setErrorMsg(e.message)
      }
    },
  })

  const onSave = useCallback(() => {
    // update trip with new originPlaceId
    mutate({
      tripId,
      originPlaceId: tripOriginRef.current?.id,
    })
  }, [mutate, tripId, tripOriginRef])

  useEffect(() => {
    inputRef.current?.focus()
    return () => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_SUGGESTED_SEARCH_ITEMS,
        searchItems: [],
      })
    }
  }, [globalSearchDispatch])

  return (
    <>
      <ModalHeaderWrapper
        title="Edit departure location"
        onCloseButtonClick={isSaving ? noop : closeModal}
        onBackButtonClick={isSaving && goBack ? noop : goBack}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={8}>
            <TripPlaceFieldDesktop
              onSelect={setTripOrigin}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              disabled={isSaving}
            />
            {errorMsg && (
              <MessageBanner kind="critical" description={errorMsg} />
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Save',
          disabled: isSaving || !tripOrigin,
          onClick: onSave,
        }}
        secondaryActionProps={{
          children: 'Cancel',
          disabled: isSaving,
          onClick: closeModal,
        }}
      />
    </>
  )
}

export default UpdateTripOrigin
